/**
 * Pure functions that convert structured analysis data to clean plain text
 * for clipboard. All output has [N] citation markers, markdown formatting,
 * and PII markers stripped.
 */

function stripCitations(text: string): string {
  return text.replace(/\[\d+\]/g, '').replace(/\s{2,}/g, ' ').trim();
}

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')   // bold
    .replace(/\*(.*?)\*/g, '$1')        // italic
    .replace(/^#{1,4}\s+/gm, '')        // headings
    .replace(/^[-*]\s+/gm, '- ')        // normalize bullets
    .trim();
}

/** Strip {{PII:TYPE}}content{{/PII}} or {{/PII:TYPE}} markers, keeping inner text. */
export function stripPiiMarkers(text: string): string {
  return text.replace(/\{\{PII:[A-Z_]+\}\}([\s\S]*?)\{\{\/P(?:II|PI)(?::[A-Z_]+)?\}\}/g, '$1');
}

export function clean(text: string | undefined | null): string {
  if (!text) return '';
  return stripMarkdown(stripPiiMarkers(stripCitations(text)));
}

// ── Chronology ──

/** Format an ISO date string into a readable date (e.g. "11 February 2025"). */
function readableDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatTimestamp(ts: string | { start: string; end?: string } | undefined): string {
  if (!ts) return '';
  if (typeof ts === 'string') return readableDate(ts);
  return ts.end ? `${readableDate(ts.start)} – ${readableDate(ts.end)}` : readableDate(ts.start);
}

/** Convert a raw event type like "gp_telephone_consultation" to a readable label. */
function readableType(type: string): string {
  const labels: Record<string, string> = {
    hospital_visit: 'Hospital Visit', outpatient_appointment: 'Outpatient',
    emergency_visit: 'Emergency', surgical_episode: 'Surgery',
    treatment_course: 'Treatment', admission: 'Admission',
    discharge: 'Discharge', diagnosis: 'Diagnosis',
    major_procedure: 'Procedure', medication_routine: 'Medication',
    vital_signs: 'Vitals', lab_result: 'Lab Result',
    consultation: 'Consultation', imaging_study: 'Imaging',
    observation: 'Observation', administrative: 'Administrative',
    medication: 'Medication', investigation: 'Investigation',
    procedure: 'Procedure', other: 'General',
    gp_visit: 'GP Visit', gp_telephone_consultation: 'GP Phone',
    prescription_renewal: 'Prescription', immunisation: 'Immunisation',
    health_screening: 'Screening', sight_test: 'Sight Test',
    dental_appointment: 'Dental', physiotherapy: 'Physiotherapy',
    mental_health_review: 'Mental Health', allied_health: 'Allied Health',
    nursing_care: 'Nursing', referral_letter: 'Referral',
    correspondence: 'Correspondence', radiology_routine: 'Routine Imaging',
    blood_test_routine: 'Routine Bloods', sick_note: 'Sick Note',
    treatment_change: 'Treatment Change',
    clinical_deterioration: 'Deterioration', clinical_improvement: 'Improvement',
    significant_investigation: 'Investigation',
  };
  return labels[type.toLowerCase()] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/** Insert line breaks before known clinical section headers for plain text. */
function structureDescription(text: string): string {
  const headers = [
    'History', 'Examination', 'Assessment', 'Plan', 'Investigations',
    'Diagnosis', 'Treatment', 'Medications', 'Management', 'Follow-up',
    'Referral', 'Advice', 'Outcome', 'Observations', 'Vital signs',
    'Blood results', 'Imaging', 'Recommendations', 'Prognosis',
    'Discharge', 'Biochemistry', 'Clinical', 'Dietary assessment',
    'Anthropometry', 'GP actions requested', 'Disposition',
  ];
  const escaped = headers.sort((a, b) => b.length - a.length).map(h => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`([.;])\\s+(${escaped.join('|')})\\s*:`, 'gi');
  return text.replace(re, (_m, boundary: string, header: string) => `${boundary}\n${header}:`);
}

export function formatChronologyEvent(event: any): string {
  const date = formatTimestamp(event.timestamp);
  const type = event.type ? `[${readableType(event.type)}]` : '';
  const title = clean(event.title || '');
  const meta = [clean(event.clinician), clean(event.location)].filter(Boolean).join(', ');
  const header = [date, type, title].filter(Boolean).join(' — ');
  const desc = structureDescription(clean(event.description || ''));
  const parts = [header];
  if (meta) parts.push(meta);
  if (desc && desc !== title) parts.push(desc);
  return parts.join('\n');
}

export function formatChronologySummaries(events: any[]): string {
  return events
    .map((event) => {
      const date = formatTimestamp(event.timestamp);
      const type = event.type ? `[${readableType(event.type)}]` : '';
      const title = clean(event.title || '');
      const meta = [clean(event.clinician), clean(event.location)].filter(Boolean).join(', ');
      const header = [date, type, title].filter(Boolean).join(' — ');
      const summary = clean(event.summary || event.description || '');
      const parts = [header];
      if (meta) parts.push(meta);
      if (summary && summary !== title) parts.push(summary);
      return parts.join('\n');
    })
    .join('\n\n');
}

export function formatFullChronology(events: any[]): string {
  return events.map(formatChronologyEvent).join('\n\n');
}

// ── HTML formatters (rich paste into Word / Google Docs / Outlook) ──

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Convert newlines within a description to <br> tags for HTML cells. */
function nlToBr(text: string): string {
  return escapeHtml(text).replace(/\n/g, '<br>');
}

/** HTML version of a single chronology event — bold date/title, italic meta. */
export function formatChronologyEventHtml(event: any): string {
  const date = formatTimestamp(event.timestamp);
  const type = event.type ? `[${readableType(event.type)}]` : '';
  const title = clean(event.title || '');
  const meta = [clean(event.clinician), clean(event.location)].filter(Boolean).join(', ');
  const desc = structureDescription(clean(event.description || ''));

  let html = `<p style="margin:0 0 2pt 0"><b>${escapeHtml(date)}</b>`;
  if (type) html += ` &mdash; <b>${escapeHtml(type)}</b>`;
  if (title) html += ` &mdash; ${escapeHtml(title)}`;
  html += '</p>';
  if (meta) html += `<p style="margin:0 0 2pt 0"><i>${escapeHtml(meta)}</i></p>`;
  if (desc && desc !== title) html += `<p style="margin:0 0 6pt 0">${nlToBr(desc)}</p>`;
  return html;
}

/** HTML rich-text summaries — bold headers, italic clinician lines. */
export function formatChronologySummariesHtml(events: any[]): string {
  const items = events.map((event) => {
    const date = formatTimestamp(event.timestamp);
    const type = event.type ? `[${readableType(event.type)}]` : '';
    const title = clean(event.title || '');
    const meta = [clean(event.clinician), clean(event.location)].filter(Boolean).join(', ');
    const summary = clean(event.summary || event.description || '');

    let html = `<p style="margin:0 0 2pt 0"><b>${escapeHtml(date)}</b>`;
    if (type) html += ` &mdash; <b>${escapeHtml(type)}</b>`;
    if (title) html += ` &mdash; ${escapeHtml(title)}`;
    html += '</p>';
    if (meta) html += `<p style="margin:0 0 2pt 0"><i>${escapeHtml(meta)}</i></p>`;
    if (summary && summary !== title) html += `<p style="margin:0 0 6pt 0">${nlToBr(summary)}</p>`;
    return html;
  });
  return items.join('');
}

/** HTML rich-text full chronology. */
export function formatFullChronologyHtml(events: any[]): string {
  return events.map(formatChronologyEventHtml).join('');
}

/** HTML table format — ideal for pasting into Word as a structured table. */
export function formatChronologyTableHtml(events: any[]): string {
  const headerStyle = 'style="background-color:#1b93a3;color:#ffffff;font-weight:bold;padding:6pt 8pt;border:1pt solid #8de0e9;text-align:left"';
  const cellStyle = 'style="padding:6pt 8pt;border:1pt solid #d1d5db;vertical-align:top"';

  let html = '<table style="border-collapse:collapse;width:100%;font-family:Calibri,Arial,sans-serif;font-size:10pt">';
  html += '<thead><tr>';
  html += `<th ${headerStyle}>Date</th>`;
  html += `<th ${headerStyle}>Type</th>`;
  html += `<th ${headerStyle}>Title</th>`;
  html += `<th ${headerStyle}>Clinician / Location</th>`;
  html += `<th ${headerStyle}>Summary</th>`;
  html += '</tr></thead><tbody>';

  events.forEach((event, i) => {
    const rowBg = i % 2 === 0 ? '#ffffff' : '#f3f4f6';
    const rs = `style="padding:6pt 8pt;border:1pt solid #d1d5db;vertical-align:top;background-color:${rowBg}"`;

    const date = escapeHtml(formatTimestamp(event.timestamp));
    const type = event.type ? escapeHtml(readableType(event.type)) : '';
    const title = escapeHtml(clean(event.title || ''));
    const meta = escapeHtml([clean(event.clinician), clean(event.location)].filter(Boolean).join(', '));
    const summary = nlToBr(clean(event.summary || event.description || ''));

    html += `<tr>`;
    html += `<td ${rs}>${date}</td>`;
    html += `<td ${rs}>${type}</td>`;
    html += `<td ${rs}>${title}</td>`;
    html += `<td ${rs}>${meta}</td>`;
    html += `<td ${rs}>${summary}</td>`;
    html += `</tr>`;
  });

  html += '</tbody></table>';
  return html;
}

/** Plain-text table fallback for editors that don't support HTML paste. */
export function formatChronologyTablePlain(events: any[]): string {
  const rows = events.map((event) => {
    const date = formatTimestamp(event.timestamp);
    const type = event.type ? readableType(event.type) : '';
    const title = clean(event.title || '');
    const meta = [clean(event.clinician), clean(event.location)].filter(Boolean).join(', ');
    const summary = clean(event.summary || event.description || '').replace(/\n/g, ' ');
    return `${date}\t${type}\t${title}\t${meta}\t${summary}`;
  });
  return `Date\tType\tTitle\tClinician / Location\tSummary\n${rows.join('\n')}`;
}

// ── Narrative overview + section summaries ──

export function formatNarrativeOverview(
  overviewSummary: string,
  sections: Array<{ title: string; summary: string }>,
): string {
  const parts: string[] = ['CASE OVERVIEW', clean(overviewSummary), ''];
  for (const section of sections) {
    parts.push(clean(section.title));
    parts.push(clean(section.summary));
    parts.push('');
  }
  return parts.join('\n').trim();
}

export function formatNarrativeOverviewHtml(
  overviewSummary: string,
  sections: Array<{ title: string; summary: string }>,
): string {
  let html = `<h2 style="margin:0 0 6pt 0;font-size:12pt">Case Overview</h2>`;
  html += `<p style="margin:0 0 12pt 0">${nlToBr(clean(overviewSummary))}</p>`;
  for (const section of sections) {
    html += `<h3 style="margin:12pt 0 4pt 0;font-size:11pt">${escapeHtml(clean(section.title))}</h3>`;
    html += `<p style="margin:0 0 8pt 0">${nlToBr(clean(section.summary))}</p>`;
  }
  return html;
}

// ── Case Summary ──

export function formatCaseSummary(summary: string): string {
  return clean(summary);
}

// ── Index Event (Causation) ──

export function formatIndexEvent(indexEvent: any): string {
  if (!indexEvent) return '';
  const parts = ['Index Event'];
  if (indexEvent.severity) parts[0] += ` (${indexEvent.severity})`;
  if (indexEvent.event || indexEvent.description) {
    parts.push(clean(indexEvent.event || indexEvent.description));
  }
  if (indexEvent.timestamp) parts.push(`Date: ${indexEvent.timestamp}`);
  if (indexEvent.description && indexEvent.event) {
    parts.push(clean(indexEvent.description));
  }
  return parts.join('\n');
}

// ── Generic analysis panel formatter ──

export function formatAnalysisItem(item: any, title: string): string {
  const cleaned = clean(title);
  if (typeof item === 'string') return cleaned;
  // Include common sub-fields if present
  const extras: string[] = [];
  if (item.description && clean(item.description) !== cleaned) extras.push(clean(item.description));
  if (item.explanation) extras.push(clean(item.explanation));
  if (item.rationale) extras.push(clean(item.rationale));
  if (item.impact) extras.push(`Impact: ${clean(item.impact)}`);
  if (item.severity) extras.push(`Severity: ${item.severity}`);
  if (extras.length === 0) return cleaned;
  return cleaned + '\n   ' + extras.join('\n   ');
}

export function formatAnalysisCategory(
  label: string,
  items: any[],
  getTitle: (item: any) => string,
): string {
  if (items.length === 0) return '';
  const lines = [`${label}:`];
  items.forEach((item, i) => {
    const title = getTitle(item);
    lines.push(`  ${i + 1}. ${formatAnalysisItem(item, title)}`);
  });
  return lines.join('\n');
}

export function formatFullPanel(
  panelName: string,
  summary: string | undefined,
  categories: Array<{ label: string; items: any[]; getTitle: (item: any) => string }>,
): string {
  const parts: string[] = [panelName, '='.repeat(panelName.length)];
  if (summary) parts.push('', clean(summary));
  for (const cat of categories) {
    const formatted = formatAnalysisCategory(cat.label, cat.items, cat.getTitle);
    if (formatted) parts.push('', formatted);
  }
  return parts.join('\n');
}

// ── Objective output sections ──

export function formatOutputSection(title: string, content: string): string {
  return `${title}\n${clean(content)}`;
}

export function formatFullOutput(
  summary: string | undefined,
  sections: Array<{ title: string; content: string }>,
  conclusion: string | undefined,
): string {
  const parts: string[] = [];
  if (summary) parts.push(`Summary\n${clean(summary)}`);
  for (const s of sections) {
    parts.push(formatOutputSection(s.title, s.content));
  }
  if (conclusion) parts.push(`Conclusion\n${clean(conclusion)}`);
  return parts.join('\n\n');
}
